import {FC, useContext, useRef, useState} from 'react';
import {useSetRecoilState} from 'recoil';
import {useForm, SubmitHandler} from 'react-hook-form';
import {toast} from 'react-toastify';

import {Button, Stack, Typography} from '@mui/material';
import ControlledInput from '../../../../ui/controlled-input/ControlledInput';
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton';

import {EventBusContext} from '../../../../providers/EventBus';

import {devicesQueryTrigger} from '../../../../store/deviceModels/atoms';

import {DictionaryService} from '../../../../api/services/dictionary.service';

import {IDevice} from '../../../../shared/types/device.types';
import {ModalContainer} from '../../../modals/ModalContainer';
import {ConfirmAction} from '../../../modals/ConfirmAction';

interface IEditDevice {
  handleClose: () => void;
  setLoading: (arg: boolean) => void;
  loading: boolean;
  device: IDevice;
}

const requiredStyle = {
  color: '#EB3333',
};

const labelStyle = {
  color: '#00203399',
};

export const EditDevice: FC<IEditDevice> = ({handleClose, setLoading, loading, device}) => {
  const {
    control,
    handleSubmit,
    formState: {isDirty, isValid},
  } = useForm({
    mode: 'onChange',
    defaultValues: device,
  });
  const triggerDeviceQuery = useSetRecoilState(devicesQueryTrigger);
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const {bus} = useContext(EventBusContext);

  const onSubmit: SubmitHandler<IDevice> = async (data, e) => {
    try {
      setLoading(true);

      await DictionaryService.updateDevice(data);

      toast.success('Данные изменены', {
        containerId: 'default',
        autoClose: 2000,
        hideProgressBar: true,
        pauseOnHover: false,
        draggable: false,
      });

      triggerDeviceQuery((v) => v + 1);

      setTimeout(() => {
        setLoading(false);
        handleClose();
      });
    } catch (err: any) {
      bus?.emit('error', [err]);

      setLoading(false);
    }
  };

  const verifyHandler = async (setLoading: (arg: boolean) => void) => {
    try {
      setLoading(true);

      await DictionaryService.verifyDevice(device.id);

      triggerDeviceQuery((v) => v + 1);
      toast.success('Устройство верифицировано', {
        containerId: 'default',
        autoClose: 2000,
        hideProgressBar: true,
        pauseOnHover: false,
        draggable: false,
      });
      setLoading(false);
      setOpen(false);
    } catch (err) {
      setLoading(false);
      bus?.emit('error', [err]);
    }
  };

  return (
    <form style={{height: '100%'}} ref={ref} onSubmit={handleSubmit(onSubmit)}>
      <Stack sx={{height: '100%'}} direction="column" justifyContent="space-between">
        <Stack sx={{height: '100%'}} direction="column" spacing={2}>
          <Stack direction="row" sx={{justifyContent: 'flex-start'}}>
            <Typography variant="h5">Редактировать устройство №{device.id}</Typography>
          </Stack>
          <Stack direction="row">
            <Stack direction="column" sx={{width: '100%'}} spacing={0.25}>
              <Stack direction="row" spacing={0.25}>
                <Typography sx={labelStyle} variant="subtitle2">
                  Мобильный оператор
                </Typography>
                <Typography sx={requiredStyle} variant="subtitle2">
                  *
                </Typography>
              </Stack>
              <ControlledInput
                control={control}
                name="operator"
                disabled={loading}
                size="small"
                rules={{
                  required: 'Обязательно для заполнения',
                }}
              />
            </Stack>
          </Stack>
          <Stack direction="row">
            <Stack direction="column" sx={{width: '100%'}} spacing={0.25}>
              <Stack direction="row" spacing={0.25}>
                <Typography sx={labelStyle} variant="subtitle2">
                  Год производства
                </Typography>
                <Typography sx={requiredStyle} variant="subtitle2">
                  *
                </Typography>
              </Stack>
              <ControlledInput
                control={control}
                name="createdYear"
                size="small"
                disabled={loading}
                sx={{borderRadius: '0 !important'}}
                rules={{
                  required: 'Обязательно для заполнения',
                  pattern: {
                    value: /^[0-9]+$/,
                    message: 'Только цифры',
                  },
                }}
              />
            </Stack>
          </Stack>
          <Stack direction="row">
            <Stack direction="column" sx={{width: '100%'}} spacing={0.25}>
              <Stack direction="row" spacing={0.25}>
                <Typography sx={labelStyle} variant="subtitle2">
                  Заводской номер
                </Typography>
                <Typography sx={requiredStyle} variant="subtitle2">
                  *
                </Typography>
              </Stack>
              <ControlledInput
                control={control}
                name="serial"
                disabled={loading}
                size="small"
                rules={{
                  required: 'Обязательно для заполнения',
                }}
              />
            </Stack>
          </Stack>
          <Stack direction="row">
            <Stack direction="column" sx={{width: '100%'}} spacing={0.25}>
              <Stack direction="row" spacing={0.25}>
                <Typography sx={labelStyle} variant="subtitle2">
                  IP
                </Typography>
              </Stack>
              <ControlledInput control={control} name="ip" disabled={loading} size="small" />
            </Stack>
          </Stack>
          <Stack direction="row">
            <Stack direction="column" sx={{width: '100%'}} spacing={0.25}>
              <Stack direction="row" spacing={0.25}>
                <Typography sx={labelStyle} variant="subtitle2">
                  Номер пломбы
                </Typography>
                <Typography sx={requiredStyle} variant="subtitle2">
                  *
                </Typography>
              </Stack>
              <ControlledInput
                control={control}
                name="sealNumber"
                size="small"
                disabled={loading}
                sx={{borderRadius: '0 !important'}}
                rules={{
                  required: 'Обязательно для заполнения',
                }}
              />
            </Stack>
          </Stack>
          <Stack direction="row">
            <Stack direction="column" sx={{width: '100%'}} spacing={0.25}>
              <Stack direction="row" spacing={0.25}>
                <Typography sx={labelStyle} variant="subtitle2">
                  Номер Сим-карты
                </Typography>
              </Stack>
              <ControlledInput
                control={control}
                name="simIcc"
                size="small"
                disabled={loading}
                sx={{borderRadius: '0 !important'}}
                rules={{
                  pattern: {
                    value: /^[0-9]+$/,
                    message: 'Только цифры',
                  },
                }}
              />
            </Stack>
          </Stack>
          <Stack direction="row">
            <Stack direction="column" sx={{width: '100%'}} spacing={0.25}>
              <Stack direction="row" spacing={0.25}>
                <Typography sx={labelStyle} variant="subtitle2">
                  Показания
                </Typography>
                <Typography sx={requiredStyle} variant="subtitle2">
                  *
                </Typography>
              </Stack>
              <Stack direction="row" spacing={2} justifyContent="space-between">
                <ControlledInput
                  control={control}
                  name="t1"
                  disabled={loading}
                  label="T1"
                  size="small"
                  rules={{
                    required: 'Обязательно для заполнения',
                    pattern: {
                      value: /^[0-9]+$/,
                      message: 'Только цифры',
                    },
                  }}
                />
                <ControlledInput
                  control={control}
                  name="t2"
                  disabled={loading}
                  label="T2"
                  size="small"
                  rules={{
                    required: 'Обязательно для заполнения',
                    pattern: {
                      value: /^[0-9]+$/,
                      message: 'Только цифры',
                    },
                  }}
                />
                <ControlledInput
                  control={control}
                  name="t3"
                  disabled={loading}
                  label="T3"
                  size="small"
                  rules={{
                    required: 'Обязательно для заполнения',
                    pattern: {
                      value: /^[0-9]+$/,
                      message: 'Только цифры',
                    },
                  }}
                />
              </Stack>
            </Stack>
          </Stack>
        </Stack>
        <Stack direction="row" sx={{justifyContent: 'space-between', mt: 4}}>
          <Stack direction="row" spacing={2}>
            <LoadingButton
              onClick={() => {
                (ref?.current as any).requestSubmit();
              }}
              variant="outlined"
              color="success"
              disabled={!(isDirty && isValid)}
              loading={loading}
            >
              Сохранить изменения
            </LoadingButton>
            <LoadingButton
              onClick={() => {
                setOpen(true);
              }}
              variant="outlined"
              color="primary"
              disabled={!isValid}
              loading={loading}
            >
              Верифицировать
            </LoadingButton>
          </Stack>
          <Button onClick={handleClose} variant="outlined" color="error" disabled={loading}>
            Отмена
          </Button>
        </Stack>
      </Stack>
      {open && (
        <ModalContainer
          sideEffect={verifyHandler}
          renderModal={(sideEffect, loading) => (
            <ConfirmAction
              loading={loading}
              open={open}
              handleClose={() => setOpen(false)}
              handleSideEffect={sideEffect}
              title="Вы уверены? После данного действия внести изменения по устройству будет невозможно"
              buttonTitle="Сохранить"
            />
          )}
        />
      )}
    </form>
  );
};
